import {
    isObservableArray,
    isObservableMap,
    isObservableObject,
    observable,
} from 'mobx'
import { difference } from '../../utils/set'
import { isPlainObject, mapFrom } from '../../utils/utils'
import { ClassData } from './ClassData'
import { DefinitionData } from './DefinitionData'
import { FieldData } from './FieldData'

export const updateInstanceWithData = (
    instance,
    data,
    propertynames,
    deeppropertynames
) => {
    // helper for updating an instance with data that is similarly shaped but comes
    // from e.g. a JSON parse, where the Map type is lost and is given as a plain object
    // for more deeply nested objects that should have deeper observability, like a
    // record's fields, we have deeppropertynames to do a deepUpdate
    propertynames.map(propertyname => {
        if (data.hasOwnProperty(propertyname)) {
            if (instance[propertyname] instanceof Map) {
                if (data[propertyname] === null) {
                    instance[propertyname] = new Map()
                } else {
                    instance[propertyname] = new Map(Object.entries(data[propertyname]))
                }
            } else if (isObservableMap(instance[propertyname])) {
                if (data[propertyname] === null) {
                    instance[propertyname].replace({})
                } else {
                    instance[propertyname].replace(data[propertyname])
                }
            } else if (isObservableArray(instance[propertyname])) {
                if (data[propertyname] === null) {
                    instance[propertyname].replace([])
                } else {
                    instance[propertyname].replace(data[propertyname])
                }
            } else {
                instance[propertyname] = data[propertyname]
            }
        }
        return undefined
    })
    if (deeppropertynames) {
        deeppropertynames.map(propertyname => {
            if (data.hasOwnProperty(propertyname)) {
                instance[propertyname] = _deepUpdate(
                    instance[propertyname],
                    data[propertyname]
                )
            }
            return undefined
        })
    }
}

const _deepUpdate = (instance, data_) => {
    let data = data_
    if (isPlainObject(data) || isObservableObject(data)) {
        data = new Map(Object.entries(data))
    }
    if (data instanceof Map || isObservableMap(data)) {
        return _deepUpdateMap(instance, data)
    }
    if (Array.isArray(data) || isObservableArray(data)) {
        return _deepUpdateArray(instance, data)
    }
    return _deepUpdateValue(instance, data)
}

const _deepUpdateValue = (instance, data) => {
    if (data === null && isObservableMap(instance)) {
        instance.clear()
        return instance
    }
    if (data === null && isObservableArray(instance)) {
        instance.clear()
        return instance
    }
    instance = data
    return instance
}

const _deepUpdateArray = (instance, data) => {
    if (!isObservableArray(instance)) {
        // data type changed
        instance = observable(mapFrom(data))
        return instance
    }
    // update elements
    data.forEach((element, index) => {
        instance[index] = _deepUpdate(instance[index], data[index])
    })
    // remove unmatched elements
    while (data.length < instance.length) {
        instance.pop()
    }
    return instance
}

const _deepUpdateMap = (instance, data) => {
    if (!isObservableMap(instance)) {
        // data type changed
        instance = observable(mapFrom(data))
        return instance
    }
    // update elements
    for (let [key, value] of data) {
        instance.set(key, _deepUpdate(instance.get(key), value))
    }
    // remove unmatched elements
    for (let [key] of instance) {
        if (!data.has(key)) {
            instance.delete(key)
        }
    }
    return instance
}

// Field options `values` looks like
//
// values: [
//     {"key": "r", "en": "Red", "nl": "Rood"},
//     {"key": "g", "en": "Green", "nl": "Groen"},
//     {"key": "b", "en": "Blue", "nl": "Blauw"},
// ]
export const listFromValues = (values, language) => {
    // from a values structure, get a list for a single language
    // or the key if value wasn't specified
    if (!values) return []
    return values.map(value => (language ? value[language] : value['key']))
}

export const listFromValuesWithKeyFallback = (values, language) => {
    // from a values structure, get a list for a single language
    // or the key if value wasn't specified
    if (!values) return []
    return values.map(value =>
        language && value[language].length ? value[language] : value['key']
    )
}

export const getAllDefinitionFieldGids = (definition, datastore) => {
    let field_gids = new Set()
    if (!definition) return field_gids
    definition.classes.forEach(classgid => {
        let class_ = datastore.classes.get(classgid)
        if (class_) {
            class_.fields.forEach(fieldgid => {
                field_gids.add(fieldgid)
            })
        }
    })
    definition.fields.forEach(fieldgid => {
        field_gids.add(fieldgid)
    })
    return field_gids
}

export const changeDefinitionLostDataFields = (
    new_definition,
    old_definition,
    datastore
) => {
    const new_fields_gids = getAllDefinitionFieldGids(new_definition, datastore)
    const old_fields_gids = getAllDefinitionFieldGids(old_definition, datastore)
    const lost_fields_gids = difference(old_fields_gids, new_fields_gids)
    const lost_fields = Array.from(lost_fields_gids).map(lost_field_gid => {
        return datastore.fields.get(lost_field_gid)
    })
    return lost_fields
}

export const changeBaseDefinitionLostDataFields = (
    new_base_definition,
    extended_definition,
    old_base_definition,
    datastore
) => {
    // After a base change the API keeps extension extras and drops old-base items.
    const old_base_fields = new Set(Array.from(old_base_definition.fields))
    const old_base_classes = new Set(Array.from(old_base_definition.classes))
    const after_definition = {
        fields: [
            ...Array.from(new_base_definition.fields),
            ...Array.from(extended_definition.fields).filter(
                field_gid => !old_base_fields.has(field_gid)
            ),
        ],
        classes: [
            ...Array.from(new_base_definition.classes),
            ...Array.from(extended_definition.classes).filter(
                class_gid => !old_base_classes.has(class_gid)
            ),
        ],
    }
    return changeDefinitionLostDataFields(
        after_definition,
        extended_definition,
        datastore
    )
}

export const formatItemName = item => {
    if (!item || !item.name) return ''

    let name = item.name

    if (item.unit) {
        name += ' [' + item.unit + ']'
    }
    return name
}

export const formatItemLabel = (item, language) => {
    if (!item || !item.label) return ''

    let label = item.label.get(language)
    if (!label) return ''

    if (item.unit) {
        label += ' [' + item.unit + ']'
    }
    return label
}

export const formatItemTitle = (item, language, labelView, emptyText) => {
    let title
    if (labelView === 'labels') {
        title = formatItemLabel(item, language)
        if (!title) {
            title = formatItemName(item)
        }
    } else {
        title = formatItemName(item)
    }

    if (!title.length && emptyText !== undefined) {
        title = emptyText
    }

    return title
}

export const hasValuesOption = field => {
    return field['options'].get('values') && field['options'].get('values').length
}

export const getSchemaitemType = item => {
    return item instanceof DefinitionData
        ? 'definition'
        : item instanceof ClassData
          ? 'class'
          : item instanceof FieldData
            ? 'field'
            : undefined
}

export const getSchemaitemStore = (gid, datastore) => {
    return datastore.definitions.has(gid)
        ? datastore.definitions
        : datastore.classes.has(gid)
          ? datastore.classes
          : datastore.fields.has(gid)
            ? datastore.fields
            : null
}

// Returns the gid of the base (committed) item: a working copy resolves to its
// original, a base or brand-new item resolves to its own gid.
export const resolveBaseGid = item => {
    if (item.is_new) return item.gid
    return item.original ? item.original : item.gid
}

// is_extended only exists on definitions; an extended definition is its own
// item, not a base/working-copy pair, so it can never be "the same" as another.
const isExtendedDefinition = item =>
    getSchemaitemType(item) === 'definition' && item.is_extended

export const isSameSchemaItem = (a, b) => {
    if (!a || !b) return false
    if (a.gid === b.gid) return true
    if (isExtendedDefinition(a) || isExtendedDefinition(b)) return false
    return resolveBaseGid(a) === resolveBaseGid(b)
}

export const normalizeMetadataSearchText = text => {
    // only lower case letters, digits, dots, spaces, and underscores
    return text
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9._ ]/g, '_')
}

export const setMetadataField = (
    metadata,
    languages,
    name,
    type,
    is_localized,
    value,
    optional_language
) => {
    const metadatafield = metadata.get(name)
    if (
        metadatafield &&
        metadatafield['type'] === type &&
        metadatafield['is_localized'] === is_localized &&
        (metadatafield['value'] === value ||
            (is_localized && metadatafield['value'][optional_language] === value))
    ) {
        return [metadata, false]
    }

    const new_metadatafield = {
        type: type,
        is_localized: is_localized,
        value: is_localized ? {} : value,
    }
    if (is_localized) {
        if (metadatafield && metadatafield['is_localized']) {
            new_metadatafield['value'] = {
                ...metadatafield['value'],
            }
        } else {
            for (const language of languages) {
                new_metadatafield['value'][language] = metadatafield
                    ? metadatafield['value']
                    : value
            }
        }
        new_metadatafield['value'][optional_language] = value
    }

    let new_metadata = new Map(metadata)
    new_metadata.set(name, new_metadatafield)
    return [new_metadata, true]
}

export const clearMetadataField = (metadata, name) => {
    if (!metadata.has(name)) return [metadata, false]
    let new_metadata = new Map(metadata)
    new_metadata.delete(name)
    return [new_metadata, true]
}

export const testMetadata = new Map(
    Object.entries({
        'test.name': {
            type: 'textline',
            is_localized: false,
            value: 'test',
        },
        'test.localname': {
            type: 'textline',
            is_localized: true,
            value: { en: 'test EN', nl: 'test NL' },
        },
    })
)

export const testMetadataFields = {
    testClearUnchanged: (metadata, languages) => {
        return clearMetadataField(metadata, 'unknown.name')
    },

    testClearName: (metadata, languages) => {
        return clearMetadataField(metadata, 'test.name')
    },

    testClearLocalname: (metadata, languages) => {
        return clearMetadataField(metadata, 'test.localname')
    },

    testSetUnchanged: (metadata, languages) => {
        return setMetadataField(
            metadata,
            languages,
            'test.name',
            'textline',
            false,
            'test',
            'nl'
        )
    },

    testSetChanged: (metadata, languages) => {
        return setMetadataField(
            metadata,
            languages,
            'test.name',
            'textline',
            false,
            'changed',
            'nl'
        )
    },

    testSetLocalized: (metadata, languages) => {
        return setMetadataField(
            metadata,
            languages,
            'test.name',
            'textline',
            true,
            'localized NL',
            'nl'
        )
    },

    testSetLocalUnchanged: (metadata, languages) => {
        return setMetadataField(
            metadata,
            languages,
            'test.localname',
            'textline',
            true,
            'test NL',
            'nl'
        )
    },

    testSetLocalChanged: (metadata, languages) => {
        return setMetadataField(
            metadata,
            languages,
            'test.localname',
            'textline',
            true,
            'changed NL',
            'nl'
        )
    },

    testSetLocalUnlocalized: (metadata, languages) => {
        return setMetadataField(
            metadata,
            languages,
            'test.localname',
            'textline',
            false,
            'changed',
            'nl'
        )
    },
}

export const sortItemsNewestFirst = (language, labelView) => {
    const sortValue = item => {
        if (!item) {
            return '???'
        }
        let value = formatItemTitle(item, language, labelView)
        if (item.is_new) {
            value = '!!!' + value // this makes sure new values sort at the top
        }
        return value
    }

    const _sort = (a, b) => {
        let sortValueA = sortValue(a)
        let sortValueB = sortValue(b)

        return sortValueA.localeCompare(sortValueB)
    }
    return _sort
}

export const sortItems = (language, labelView) => {
    const sortValue = item => {
        if (!item) {
            return '???'
        }
        let value = formatItemTitle(item, language, labelView)
        return value
    }

    const _sort = (a, b) => {
        let sortValueA = sortValue(a)
        let sortValueB = sortValue(b)

        return sortValueA.localeCompare(sortValueB)
    }
    return _sort
}

export const sortProjectMembers = (project_members, users) => {
    project_members.sort((a, b) => {
        const a_owner = a.roles.includes('owner')
        const b_owner = b.roles.includes('owner')
        const a_api = a.roles.includes('api')
        const b_api = b.roles.includes('api')
        if (a_owner !== b_owner) return a_owner ? -1 : 1
        if (a_api !== b_api) return a_api ? 1 : -1
        const a_user = users.get(a.user_gid)
        const b_user = users.get(b.user_gid)
        if (!a_user) return -1
        if (!b_user) return 1
        return a_user.displayname.localeCompare(b_user.displayname)
    })
}

// const test = languages => {
//     for (const [testname, testfunction] of Object.entries(testMetadataFields)) {
//         let [new_metadata, is_updated] = testfunction(testMetadata, languages)
//         console.log(testname, is_updated, new_metadata)
//     }
// }
